import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  Home,
  Mail,
  Database,
  Settings,
  Shield,
  Menu,
  X,
  BarChart3,
  Download,
  Upload,
  User,
  LogOut,
  RefreshCw,
  Server,
  Code
} from 'lucide-react'
import ThemeToggle from './ThemeToggle'
import { useAuth } from '../contexts/AuthContext'
import logo from '../logo/logo.png'

const navigation = [
  { name: 'Dashboard', href: '/', icon: Home },
  { name: 'Single Validation', href: '/verification', icon: Mail },
  { name: 'List Validation', href: '/list-verification', icon: Upload },
  { name: 'Download Lists', href: '/download-lists', icon: Download },
  { name: 'List Results', href: '/results', icon: Database },
  { name: 'Lead Management', href: '/leads', icon: Database },
  { name: 'Satellite Nodes', href: '/satellites', icon: Server },
  { name: 'Server Status', href: '/admin', icon: Shield },
  { name: 'Settings', href: '/settings', icon: Settings },
  { name: 'API Access', href: '/api-access', icon: Code },
  { name: 'Updates', href: '/updates', icon: RefreshCw },
  { name: 'Profile', href: '/profile', icon: User },
]

export default function Layout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const location = useLocation()
  const { logout } = useAuth()

  const NavItem = ({ item, mobile = false }) => {
    const isActive = location.pathname === item.href
    return (
      <Link
        key={item.name}
        to={item.href}
        className={`group flex items-center gap-3 px-3 py-2.5 text-sm font-semibold rounded-xl transition-all duration-300 border ${isActive
          ? 'bg-brand-50 text-brand-700 border-brand-100 shadow-sm dark:bg-brand-500/10 dark:text-brand-400 dark:border-brand-500/20'
          : 'text-slate-600 border-transparent hover:bg-slate-50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800/50 dark:hover:text-slate-100'
          }`}
        onClick={() => mobile && setSidebarOpen(false)}
      >
        <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors duration-300 ${isActive ? 'bg-brand-600 text-white shadow-lg shadow-brand-500/30' : 'bg-slate-100 text-slate-500 group-hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-500 dark:group-hover:text-slate-300'}`}>
          <item.icon className="h-4 w-4" />
        </span>
        {item.name}
      </Link>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-slate-950/40 backdrop-blur-sm animate-fade-in" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-72 flex-col bg-white dark:bg-slate-900 shadow-2xl animate-slide-right">
          <div className="flex h-20 items-center justify-between px-6 border-b border-slate-100 dark:border-slate-800/60">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-white dark:bg-slate-800 rounded-xl flex items-center justify-center shadow-lg">
                <img src={logo} alt="Logo" className="h-8 w-8 object-contain" />
              </div>
              <h1 className="text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r from-brand-600 to-primary-600 dark:from-brand-400 dark:to-primary-400">Smart Email Verifier</h1>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <nav className="flex-1 space-y-1 px-4 py-6 overflow-y-auto">
            {navigation.map((item) => <NavItem key={item.name} item={item} mobile />)}
          </nav>
          <div className="p-4 border-t border-slate-100 dark:border-slate-800/60">
            <button
              onClick={logout}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm font-semibold text-slate-600 hover:bg-danger-50 hover:text-danger-600 rounded-xl transition-all dark:text-slate-400 dark:hover:bg-danger-900/10 dark:hover:text-danger-400"
            >
              <LogOut className="h-5 w-5" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-72 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800/60 shadow-sm">
          <div className="flex h-20 items-center px-6 border-b border-slate-100 dark:border-slate-800/60">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 bg-white dark:bg-slate-800 rounded-xl flex items-center justify-center shadow-lg">
                <img src={logo} alt="Logo" className="h-8 w-8 object-contain" />
              </div>
              <h1 className="text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r from-brand-600 to-primary-600 dark:from-brand-400 dark:to-primary-400 tracking-tight">Smart Email Verifier</h1>
            </div>
          </div>
          <nav className="flex-1 space-y-1.5 px-4 py-6 overflow-y-auto scrollbar-hide">
            {navigation.map((item) => <NavItem key={item.name} item={item} />)}
          </nav>
          <div className="p-4 border-t border-slate-100 dark:border-slate-800/60">
            <button
              onClick={logout}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm font-semibold text-slate-600 hover:bg-danger-50 hover:text-danger-600 rounded-xl transition-all dark:text-slate-400 dark:hover:bg-danger-900/10 dark:hover:text-danger-400"
            >
              <LogOut className="h-5 w-5" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-72">
        {/* Top bar */}
        <div className="glass-nav px-4 sm:px-6 lg:px-8 h-20 flex items-center gap-x-4">
          <button
            type="button"
            className="p-2.5 text-slate-600 lg:hidden hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6 items-center">
            <div className="flex flex-1">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight">
                {navigation.find(item => item.href === location.pathname)?.name || 'Smart Email Verifier'}
              </h2>
            </div>
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              <ThemeToggle />
              <div className="hidden sm:flex sm:items-center sm:gap-x-3">
                <Link
                  to="/verification"
                  className="btn btn-secondary py-2"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Quick Check
                </Link>
                <Link
                  to="/list-verification"
                  className="btn btn-primary py-2"
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Upload List
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-8">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 animate-fade-in">
            {children}
          </div>
        </main>
      </div>
    </div>
  )
} 